import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExperimentReportsDialogue } from './ExperimentReportsDialogue';
import ExperimentService from 'business/services/ExperimentService';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { LoadingModal } from 'components/modal';
import { GetPlatePadding, showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import {
  CaptureProfileOption,
  ExperimentReportOptionsDTO,
} from './ExperimentReportOptions';
import { startExperimentReportBackgroundJob } from './experimentReportBackgroundTask';

interface Props {
  reportType: string;
  setReportType: (e: string) => void;
  expIds: number[];
  onPopupBlocked?: () => void;
}

function mergeCaptureProfiles(
  experiments: ExperimentDtoClient[],
): CaptureProfileOption[] {
  const merged: CaptureProfileOption[] = [];

  experiments.forEach((experiment) => {
    experiment?.profiles?.forEach((profile) => {
      if (!merged.some((cp) => cp.id === profile.id)) {
        merged.push({
          id: profile.id,
          name: profile.name,
          selected: merged.length === 0,
        });
      }
    });
  });

  return merged;
}

export const ExperimentReportDialogueWrapper = ({
  reportType,
  setReportType,
  expIds,
  onPopupBlocked,
}: Props) => {
  const [expData, setExpData] = useState(null);
  const [mergedCaptureProfiles, setMergedCaptureProfiles] = useState<
    CaptureProfileOption[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const expIdsKey = useMemo(() => expIds.join(','), [expIds]);

  useEffect(() => {
    const fetchExperimentData = async () => {
      if (!expIds.length) {
        return;
      }

      const experimentService = new ExperimentService();
      setIsLoading(true);
      const experiments = (
        await Promise.all(expIds.map((id) => experimentService.getExperimentService(id)))
      ).filter((experiment) => experiment != null);

      if (!experiments.length) {
        setExpData(null);
        setMergedCaptureProfiles([]);
        setIsLoading(false);
        return;
      }

      setExpData(experiments[0]);
      setMergedCaptureProfiles(
        expIds.length > 1 ? mergeCaptureProfiles(experiments) : [],
      );
      setIsLoading(false);
    };

    fetchExperimentData();
  }, [expIdsKey, expIds]);

  const onStartBackgroundReport = useCallback(
    async (reportOptions: ExperimentReportOptionsDTO, saveReport: boolean) => {
      const fileName =
        expIds.length > 1
          ? `Experiment Report (${expIds.length} experiments)`
          : `${expData.expName} - Experiment Report`;

      try {
        await startExperimentReportBackgroundJob({
          experimentIds: expIds,
          reportOptions,
          saveReport,
          fileName,
          onPopupBlocked,
        });
        showAlertSnackBar(
          EAlertType.INFO,
          ESnackBarPosition.TOP,
          'Check notification panel',
          'Generating multiple experiment may take some time',
        );
      } catch {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'Unable to start experiment report generation.',
        );
      }
    },
    [expData, expIds, onPopupBlocked],
  );

  return (
    <>
      {expData && (
        <ExperimentReportsDialogue
          selectedWell={expData?.wellDetails}
          reportType={reportType}
          setReportType={setReportType}
          canvasRef={null}
          docData={expData}
          platePadding={GetPlatePadding()}
          captureProfiles={
            mergedCaptureProfiles.length ? mergedCaptureProfiles : undefined
          }
          onPopupBlocked={onPopupBlocked}
          runReportInBackground
          onStartBackgroundReport={onStartBackgroundReport}
        />
      )}
      <LoadingModal showModal={isLoading} text={'Loading...'} />
    </>
  );
};

export default ExperimentReportDialogueWrapper;
